import React, { useState, useRef, useEffect } from 'react';
import api from '../services/api';

interface SimpleImageUploaderProps {
  title: string;
  onImageUploaded?: (imageUrl: string) => void;
  existingImageUrl?: string;
}

const SimpleImageUploader: React.FC<SimpleImageUploaderProps> = ({ title, onImageUploaded, existingImageUrl }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(existingImageUrl || null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(existingImageUrl || null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Update local state when existingImageUrl prop changes
  useEffect(() => {
    setImageUrl(existingImageUrl || null);
    setPreviewUrl(existingImageUrl || null);
    // Clear the file when we get a new existing URL since it means it's already uploaded
    if (existingImageUrl) {
      setImageFile(null);
    }
  }, [existingImageUrl]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Please select a JPEG, PNG, GIF, WEBP, or SVG image.');
      return;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('File too large. Maximum size is 10MB.');
      return;
    }

    // Create preview URL
    const url = URL.createObjectURL(file);
    
    // Clean up previous preview
    if (previewUrl && imageFile) {
      URL.revokeObjectURL(previewUrl);
    }
    
    setImageFile(file);
    setPreviewUrl(url);
    setImageUrl(null); // Clear uploaded URL since we have a new file
  };

  const uploadImage = async () => {
    if (!imageFile || !onImageUploaded) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await api.post('/tours/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const uploadedUrl = response.data.image_url;
      setImageUrl(uploadedUrl);
      onImageUploaded(uploadedUrl);
      
      // Keep the preview but clear the file since it's uploaded
      setImageFile(null);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const removeImage = () => {
    // Clean up object URL to prevent memory leaks
    if (previewUrl && imageFile) {
      URL.revokeObjectURL(previewUrl);
    }
    setImageUrl(null);
    setImageFile(null);
    setPreviewUrl(null);
    if (onImageUploaded) {
      onImageUploaded('');
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (previewUrl && imageFile) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl, imageFile]);

  return (
    <div className="p-3 border border-primary-200 rounded-lg bg-primary-50">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm text-blue-700">
          🖼️ Image Upload for {title}
        </div>
        {previewUrl && (
          <button
            type="button"
            onClick={removeImage}
            className="text-red-500 hover:text-red-700 text-xs"
            title="Remove image"
          >
            🗑️ Remove
          </button>
        )}
      </div>
      
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
        onChange={handleFileSelect}
        className="hidden"
      />
      
      <div className="space-y-2">
        {/* Preview */}
        {previewUrl && (
          <div className="relative w-full h-32 rounded overflow-hidden bg-gray-100">
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-full object-contain"
              crossOrigin="anonymous"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        )}
        
        {/* Action buttons */}
        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={triggerFileSelect}
            className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
          >
            📁 {previewUrl ? 'Change' : 'Select'} Image
          </button>
          
          {imageFile && (
            <button
              type="button"
              onClick={uploadImage}
              disabled={isUploading}
              className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600 disabled:opacity-50"
            >
              {isUploading ? '⏳ Uploading...' : '💾 Upload'}
            </button>
          )}
        </div>
        
        {/* Status messages */}
        {imageUrl && !imageFile && (
          <div className="text-xs text-green-600">
            ✅ Image uploaded successfully
          </div>
        )}
        
        {imageFile && (
          <div className="text-xs text-yellow-600">
            ⚠️ Click "Upload" to save your image
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleImageUploader;
